/*
 * THE PAPER (2026-10-10): the card stock under every face of the card preview, where the white is unprinted.
 * window.PPPaper, shared by the redesign's pages (rd-finding.js: the Picture Card; rd-design.js: the back; rd-card3d.js:
 * the inside's planes) and the share page (sections/shared-card.liquid). Moved here from rd-finding.js the same day so
 * the share page could have it without loading the catalogue code. Styles: assets/card-paper.css.
 *
 * Theme only: the artists site's copy of the share page (artists-worker public/card, synced from shared-card.js) does
 * not load this, and shared-card.js does not call it, so that copy is unaffected.
 */
(function () {
  'use strict';
  if (window.PPPaper) return;
  /* ---- THE PAPER: every face of the card, where the white is unprinted (Yonatan, 9 + 10 Oct; Daniel, 9 Oct) ----------
     Yonatan, 10 Oct: the off-white "has to be the base of the whole card: front, inside, and back", and it needs "a visible
     but subtle paper texture on every face, like real card stock". So any face whose art has a plain white ground gets a
     .rdf-paper layer over its art: the front here (pictureCard), the back (rd-design.js), the inside's planes
     (rd-card3d.js). Web preview only; the print files are untouched.

     IS THE GROUND WHITE? Read once per image: the art is drawn at 60 x 80 into a canvas from a CORS copy (the catalogue
     and the theme's CDN both answer Access-Control-Allow-Origin *), and its ground is white when 60 % of the opaque samples in
     a band 1.5-5 % in from the four edges are near-white (each channel >= 236, spread <= 14). A printed ground fills the
     band (0 of 180 on a pale pink, 9 Oct); a white one leaves most of it white even with a greeting or a pattern reaching
     in: You quack me up's strip, pastel hearts and stars on white, is 80 %, which the 9 Oct line (85 %, fronts only) let
     go. A clear pixel counts as white: nothing is printed there, and the plane under it is white (You quack me up's inside
     ground is clear all over). A printed ground, a photo or art that runs to the edges fails, and so does anything that
     cannot be read: the face then shows exactly as before. A "no" is kept for the tab (sessionStorage).

     ONLY ON THE WHITE, NEVER ON THE ART (Daniel declined grain over printed artwork, 9 Oct). The layer is masked to the
     art's own near-white pixels (paperMaskOf: 256 px wide, white = min channel 228-244 and spread under 24, grown by one
     pixel so the paper meets the line work without a white rim), and it is still blended by darken, so where the mask
     is soft at an edge a colour can only keep its own value or meet the paper's, never be lightened. The 9 Oct layer had
     no mask, so its grain had to be white fleck and read as flat (Yonatan, 10 Oct: "There's no texture").

     THE TEXTURE (paperTexture): one 512 px tile drawn once per page in a canvas (no image to download), for 46 mm of card
     (background-size 50% of a 92 mm face, the rest scaled to match): #FAF5EC (brand-tokens, card-internal palette) with
     a soft mottle, a fine tooth and a few hundred short fibres, darker and lighter. Seeded, so it is the same in every
     browser and on every visit. --rdf-paper-tex on the root holds it; until it exists the layer is the flat paper. */
  const PAPER = new Map();      // src -> 0 (not white) | blob URL of its mask | a Promise of one of those
  try { const k = JSON.parse(sessionStorage.getItem('pp_paper_v3') || '{}'); Object.keys(k).forEach((u) => PAPER.set(u, 0)); } catch (e) { /* storage off: read again */ }
  let paperSave = 0;
  function paperNo(src) {
    PAPER.set(src, 0);
    clearTimeout(paperSave);
    paperSave = setTimeout(() => { try { const k = {}; PAPER.forEach((x, u) => { if (x === 0 && !/^blob:/.test(u)) k[u] = 0; }); sessionStorage.setItem('pp_paper_v3', JSON.stringify(k)); } catch (e) { /* full or off */ } }, 400);
  }
  function whiteGround(im) {
    const W = 60, H = 80, c = document.createElement('canvas');
    c.width = W; c.height = H;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(im, 0, 0, W, H);
    const px = x.getImageData(0, 0, W, H).data;
    let n = 0, w = 0;
    for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
      const fx = (i + 0.5) / W, fy = (y + 0.5) / H, ex = Math.min(fx, 1 - fx), ey = Math.min(fy, 1 - fy);
      if (!((ex >= 0.015 && ex <= 0.05 && ey >= 0.015) || (ey >= 0.015 && ey <= 0.05 && ex >= 0.015))) continue;
      const k = (y * W + i) * 4, r = px[k], g = px[k + 1], b = px[k + 2];
      n++;
      if (px[k + 3] < 128) { w++; continue; }   // nothing printed there: the plane's own white shows (an inside ground left clear)
      if (Math.min(r, g, b) >= 236 && Math.max(r, g, b) - Math.min(r, g, b) <= 14) w++;
    }
    return n > 40 && w / n >= 0.6 ? 1 : 0;
  }
  function paperMaskOf(im) {
    const W = 256, H = Math.max(1, Math.round(W * im.naturalHeight / im.naturalWidth));
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(im, 0, 0, W, H);
    const d = x.getImageData(0, 0, W, H), p = d.data, a = new Uint8ClampedArray(W * H);
    for (let i = 0; i < W * H; i++) {
      const r = p[i * 4], g = p[i * 4 + 1], b = p[i * 4 + 2], mn = Math.min(r, g, b), sp = Math.max(r, g, b) - mn;
      // white, or clear (the plane's white shows through: an inside ground left unprinted)
      a[i] = 255 * (1 - (p[i * 4 + 3] / 255) * (1 - Math.max(0, Math.min(1, (mn - 228) / 16)) * Math.max(0, Math.min(1, (24 - sp) / 10))));
    }
    for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
      let m = 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const yy = Math.min(H - 1, Math.max(0, y + dy)), xx = Math.min(W - 1, Math.max(0, i + dx));
        if (a[yy * W + xx] > m) m = a[yy * W + xx];
      }
      const k = (y * W + i) * 4;
      p[k] = p[k + 1] = p[k + 2] = 255; p[k + 3] = m;
    }
    x.putImageData(d, 0, 0);
    return c;
  }
  /* THE TUNER (Daniel, 10 Oct: he judges the tone and the texture himself; sliders over rounds of guessing). Any page
     with ?papertune=1 (kept for the tab; ?papertune=0 ends it) shows a small panel of sliders: how far the paper is
     from white (0 pure white, 1 #FAF5EC, more beyond), the mottle (soft tonal patches) and the tooth (fine grain), each on its own, the number of fibres and the size of the
     tooth, live on every face, with the values read out to pass on. Its values are kept in this
     browser only (localStorage) and change nothing for anybody else; without the flag the defaults below are what
     every visitor sees. When a look is chosen, its numbers become these defaults. */
  // Yonatan's tune, 12 Oct, set with the tuner (base #FCF9F3; about halfway from Daniel's 10 Oct pick back toward
  // white: offwhite 1, mottle 2, tooth 1.55, fibres 1.75, size 45). Mottle and tooth: 1 is the first Draft's amount.
  const PT_DEF = { offwhite: 0.65, mottle: 0.65, tooth: 0.8, fibres: 3, size: 23 };
  let PT = Object.assign({}, PT_DEF);
  let tuning = false;
  try {
    const q = new URLSearchParams(location.search).get('papertune');
    if (q === '1') sessionStorage.setItem('pp_papertune', '1');
    if (q === '0') sessionStorage.removeItem('pp_papertune');
    tuning = sessionStorage.getItem('pp_papertune') === '1';
    if (tuning) PT = Object.assign(PT, JSON.parse(localStorage.getItem('pp_papertune_v2') || '{}'));
  } catch (e) { /* storage off: the defaults */ }
  let texMade = false, texUrl = '';
  function paperTexture() {
    if (texMade) return;
    texMade = true;
    const T = 512, N = T / 2, M = PT.mottle, D = PT.tooth;
    const base = paperBase();
    let s = 0x9e3779b9;
    const rnd = () => { s = (s + 0x6D2B79F5) | 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    const lattice = (n) => { const a = new Float32Array(n * n); for (let i = 0; i < a.length; i++) a[i] = rnd() * 2 - 1; return a; };
    // tileable value noise on an n x n lattice, smooth-stepped, sampled on the N x N field
    const sample = (L, n, x, y) => {
      const fx = x * n / N, fy = y * n / N, ix = fx | 0, iy = fy | 0, tx = fx - ix, ty = fy - iy;
      const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
      const x0 = ix % n, y0 = (iy % n) * n, x1 = (ix + 1) % n, y1 = ((iy + 1) % n) * n;
      const a = L[y0 + x0], b = L[y0 + x1], c = L[y1 + x0], d = L[y1 + x1];
      return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
    };
    try {
      const m1 = lattice(3), m2 = lattice(6), t1 = lattice(170), t2 = lattice(85);
      const f = document.createElement('canvas');
      f.width = f.height = N;
      const fc = f.getContext('2d'), im = fc.createImageData(N, N), p = im.data;
      for (let y = 0; y < N; y++) for (let i = 0; i < N; i++) {
        // mottle (+-3.2 levels x M, in patches a third and a sixth of the tile), tooth (+-3 x D), and a fixed +-1.2
        // grain that dithers the mottle's slow gradients so they never band; round the base, warmer as it darkens
        const v = M * 3.2 * (sample(m1, 3, i, y) * 0.65 + sample(m2, 6, i, y) * 0.35) + D * 3 * (sample(t1, 170, i, y) * 0.6 + sample(t2, 85, i, y) * 0.4) + 1.2 * (rnd() * 2 - 1);
        const k = (y * N + i) * 4;
        p[k] = base[0] + v; p[k + 1] = base[1] + v * 1.03; p[k + 2] = base[2] + v * 1.1; p[k + 3] = 255;
      }
      fc.putImageData(im, 0, 0);
      const c = document.createElement('canvas');
      c.width = c.height = T;
      const x = c.getContext('2d');
      for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) x.drawImage(f, dx * T, dy * T, T, T);   // wrapped, so the tile has no seam
      f.width = f.height = 0;
      x.lineCap = 'round';
      const fibre = (col, a, w) => {
        const x0 = rnd() * T, y0 = rnd() * T, ang = rnd() * Math.PI * 2, len = 8 + rnd() * rnd() * 46, bend = (rnd() - 0.5) * 0.9;
        const mx = x0 + Math.cos(ang) * len / 2 - Math.sin(ang) * bend * len * 0.3, my = y0 + Math.sin(ang) * len / 2 + Math.cos(ang) * bend * len * 0.3;
        const x1 = x0 + Math.cos(ang + bend) * len, y1 = y0 + Math.sin(ang + bend) * len;
        x.strokeStyle = col + (a * (0.45 + rnd() * 0.55)).toFixed(3) + ')';
        x.lineWidth = w * (0.6 + rnd() * 0.8);
        for (let dx = -T; dx <= T; dx += T) for (let dy = -T; dy <= T; dy += T) {
          if (Math.max(x0, x1, mx) + dx < -2 || Math.min(x0, x1, mx) + dx > T + 2 || Math.max(y0, y1, my) + dy < -2 || Math.min(y0, y1, my) + dy > T + 2) continue;
          x.beginPath(); x.moveTo(x0 + dx, y0 + dy); x.quadraticCurveTo(mx + dx, my + dy, x1 + dx, y1 + dy); x.stroke();
        }
      };
      for (let n = 0; n < Math.round(300 * PT.fibres); n++) fibre('rgba(140,118,86,', 0.10, 1.3);
      for (let n = 0; n < Math.round(260 * PT.fibres); n++) fibre('rgba(255,255,255,', 0.55, 1.6);
      const root = document.documentElement.style;
      root.setProperty('--rdf-paper-k', (PT.size / 46).toFixed(3));
      root.setProperty('--rdf-paper-base', 'rgb(' + base.join(',') + ')');
      c.toBlob((b) => {
        c.width = c.height = 0;
        if (!b) return;
        if (texUrl) URL.revokeObjectURL(texUrl);
        texUrl = URL.createObjectURL(b);
        root.setProperty('--rdf-paper-tex', 'url("' + texUrl + '")');
      }, 'image/png');
    } catch (e) { /* no canvas: the flat paper */ }
  }
  // The paper's tone: offwhite 0 is pure white, 1 is #FAF5EC (the default), 2 twice as far from white, warm the same way.
  const paperBase = () => [255 - 5 * PT.offwhite, 255 - 10 * PT.offwhite, 255 - 19 * PT.offwhite].map((v) => Math.max(0, Math.round(v)));
  function paperTuner() {
    const rows = [['offwhite', 'Off-white (0 pure white, 1 = #FAF5EC)', 0, 3, 0.05], ['mottle', 'Mottle (soft tonal patches; 1 = first Draft)', 0, 5, 0.05], ['tooth', 'Tooth (fine grain; 1 = first Draft)', 0, 3, 0.05],
      ['fibres', 'Fibres', 0, 3, 0.05], ['size', 'Tooth size (mm per tile)', 15, 120, 1]];
    const box = document.createElement('div');
    box.className = 'rdf-ptune';
    box.innerHTML = '<button type="button" class="rdf-ptune__t" data-min aria-expanded="true">Paper tuner (tap to fold)</button>' + rows.map(([k, l, a, z, st]) => '<label>' + l + ' <output data-o="' + k + '"></output><input type="range" min="' + a + '" max="' + z + '" step="' + st + '" data-k="' + k + '"></label>').join('') +
      '<code data-v></code><span><button type="button" data-reset>Reset</button><button type="button" data-hide>Hide</button></span>';
    const show = () => {
      rows.forEach(([k]) => { box.querySelector('[data-k="' + k + '"]').value = PT[k]; box.querySelector('[data-o="' + k + '"]').textContent = PT[k]; });
      const base = paperBase();
      box.querySelector('[data-v]').textContent = JSON.stringify(PT) + '  base #' + base.map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase();
    };
    let t = 0;
    const redo = () => {
      try { localStorage.setItem('pp_papertune_v2', JSON.stringify(PT)); } catch (e) { /* off */ }
      show();
      clearTimeout(t);
      t = setTimeout(() => { texMade = false; paperTexture(); }, 90);
    };
    box.addEventListener('input', (e) => { const k = e.target.getAttribute('data-k'); if (k) { PT[k] = +e.target.value; redo(); } });
    box.querySelector('[data-reset]').onclick = () => { PT = Object.assign({}, PT_DEF); redo(); };
    box.querySelector('[data-hide]').onclick = () => { box.remove(); };
    // Folded, it keeps out of the way of the card's Front / Inside / Back buttons on a phone.
    box.querySelector('[data-min]').onclick = (e) => { const m = box.classList.toggle('is-min'); e.currentTarget.setAttribute('aria-expanded', String(!m)); };
    document.body.appendChild(box);
    show();
    texMade = false; paperTexture();   // the texture exists to tune even before a white card is read
  }
  if (tuning) (document.body ? paperTuner() : document.addEventListener('DOMContentLoaded', paperTuner));
  const idle = (f) => (window.requestIdleCallback || ((g) => setTimeout(g, 60)))(f, { timeout: 1500 });
  // The mask of src's white ground, or '' when its ground is not white (or cannot be read).
  function paperMask(src) {
    if (!src) return Promise.resolve('');
    const v = PAPER.get(src);
    if (v === 0) return Promise.resolve('');
    if (typeof v === 'string') return Promise.resolve(v);
    if (v) return v;
    const p = new Promise((ok) => idle(() => {
      const im = new Image();
      if (!/^blob:|^data:/.test(src)) im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = () => {
        let m = null;
        try { m = whiteGround(im) ? paperMaskOf(im) : null; } catch (e) { m = null; }
        if (!m) { paperNo(src); ok(''); return; }
        paperTexture();
        m.toBlob((b) => { m.width = m.height = 0; const u = b ? URL.createObjectURL(b) : ''; PAPER.set(src, u || 0); ok(u); }, 'image/png');
      };
      im.onerror = () => { PAPER.set(src, 0); ok(''); };
      im.src = src;
    }));
    PAPER.set(src, p);
    return p;
  }
  // Show the paper on a .rdf-paper layer, masked to src's white; done() hears whether it did. A layer re-dressed with
  // another src before the answer comes keeps the newer one's.
  // plain: the paper is decided by src's ground but laid unmasked, over a face with no art of its own (the share page's
  // note panel, which is the card's own stock: paper when the card's front is).
  function paperOn(lay, src, done, plain) {
    if (!lay) return;
    lay.setAttribute('data-for', src || '');
    if (!src) { lay.classList.remove('is-on'); return; }
    paperMask(src).then((u) => {
      if (lay.getAttribute('data-for') !== src) return;
      if (u) { lay.style.webkitMaskImage = lay.style.maskImage = plain ? 'none' : 'url("' + u + '")'; lay.classList.add('is-on'); }
      else lay.classList.remove('is-on');
      if (done) done(!!u);
    });
  }
  // An image followed by its .rdf-paper layer dresses it on load, and again when its src changes (the Picture Card's
  // front, the back in a design's box, the inside's covers, the share page's covers and flat faces).
  document.addEventListener('load', (e) => {
    const im = e.target, lay = im && im.nextElementSibling;
    // data-paper-own: a layer its page dresses itself (the builder, assets/builder-paper.js: never from a customer's photo)
    if (!im || im.tagName !== 'IMG' || !lay || !lay.classList.contains('rdf-paper') || lay.hasAttribute('data-paper-own')) return;
    // the copy the browser chose (a tile's srcset, 2026-10-20), so the paper reads the bytes already downloaded
    const src = im.currentSrc || im.getAttribute('src');
    if (!src || (lay.classList.contains('is-on') && lay.getAttribute('data-for') === src)) return;
    paperOn(lay, src, (on) => { const pc = on && im.closest('.pc'); if (pc) pc.setAttribute('data-paper', ''); });
  }, true);
  window.PPPaper = {
    on: paperOn,
    mask: paperMask,
    // the mask already made for src (a blob URL), 0 when its ground is not white, else undefined or a Promise
    cached: (src) => PAPER.get(src),
    // the values in use, for checking
    tune: () => Object.assign({ tuning }, PT)
  };
})();
